import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import { areAllRelatedBehaviorsSelected } from '../utils';
import useTreatmentPlanV3CrisisPageState from './useTreatmentPlanV3CrisisPageState';

import type { AppDispatch } from '../../../../../../store';
import type { CrisisPlanPayload } from '../../../../../common';
import type {
  TreatmentPlanV3CrisisPlansDependencies,
  UseTreatmentPlanV3CrisisPlansResult,
} from './types';

export default function useTreatmentPlanV3CrisisPlans() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const pageState = useTreatmentPlanV3CrisisPageState();

  return useTreatmentPlanV3CrisisPlansWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    ...pageState,
  });
}

export function useTreatmentPlanV3CrisisPlansWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  crisisPlans,
  behaviors,
  noCrisisPlanNeeded,
  noMaladaptiveBehavior,
  crisisPageReady,
  hasCrisisLoadError,
  assessmentPageReady,
  hasAssessmentLoadError,
  isLoading,
  isSaving,
  isBulkSaving,
}: TreatmentPlanV3CrisisPlansDependencies): UseTreatmentPlanV3CrisisPlansResult {
  const create = useCallback(
    async (data: CrisisPlanPayload) => {
      if (!treatmentPlanId || !crisisPageReady) return undefined;
      if (areAllRelatedBehaviorsSelected(behaviors, crisisPlans)) return undefined;
      const result = await dispatch(
        actions.createTreatmentPlanV3CrisisPlan({ treatmentPlanId, data, getAccessTokenSilently }),
      );
      return actions.createTreatmentPlanV3CrisisPlan.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [
      actions,
      behaviors,
      crisisPageReady,
      crisisPlans,
      dispatch,
      getAccessTokenSilently,
      treatmentPlanId,
    ],
  );

  const update = useCallback(
    async (crisisPlanId: number, data: CrisisPlanPayload) => {
      if (!treatmentPlanId || !crisisPageReady) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3CrisisPlan({
          treatmentPlanId,
          crisisPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3CrisisPlan.fulfilled.match(result);
    },
    [actions, crisisPageReady, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (crisisPlanId: number) => {
      if (!treatmentPlanId || !crisisPageReady) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3CrisisPlan({
          treatmentPlanId,
          crisisPlanId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3CrisisPlan.fulfilled.match(result);
    },
    [actions, crisisPageReady, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const clearAll = useCallback(async () => {
    if (!treatmentPlanId || !crisisPageReady) return false;
    const result = await dispatch(
      actions.deleteAllTreatmentPlanV3CrisisPlans({
        treatmentPlanId,
        crisisPlanIds: crisisPlans.map((row) => row.id),
        getAccessTokenSilently,
      }),
    );
    return actions.deleteAllTreatmentPlanV3CrisisPlans.fulfilled.match(result);
  }, [actions, crisisPageReady, crisisPlans, dispatch, getAccessTokenSilently, treatmentPlanId]);

  const setNoCrisisPlanNeeded = useCallback(
    async (nextNoCrisisPlanNeeded: boolean) => {
      if (!treatmentPlanId || !crisisPageReady) return false;
      const result = await dispatch(
        actions.setTreatmentPlanV3NoCrisisPlanNeeded({
          treatmentPlanId,
          noCrisisPlanNeeded: nextNoCrisisPlanNeeded,
          getAccessTokenSilently,
        }),
      );
      return actions.setTreatmentPlanV3NoCrisisPlanNeeded.fulfilled.match(result);
    },
    [actions, crisisPageReady, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    crisisPlans,
    behaviors,
    noCrisisPlanNeeded,
    noMaladaptiveBehavior,
    crisisPageReady,
    hasCrisisLoadError,
    assessmentPageReady,
    hasAssessmentLoadError,
    isLoading,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    setNoCrisisPlanNeeded,
  };
}
